import React, { useContext, useEffect, useState } from 'react';
import {
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom/cjs/react-router-dom.min';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import Icon from 'components/Icon';
import { POSCard, POSCardHeader, POSCardBody, POSButton } from 'components/POS';
import { useDebounceCallback } from 'hooks/useDebounceCallback';
import { AppContext } from 'shared/AppContext';
import { useCart } from 'shared/CartContext';
import CashPayment from './CashPayment';
import { useReleaseBestAvailable } from './gql/mutation/useReleaseBestAvailable';
import { useReleaseSeatsHold } from './gql/mutation/useReleaseSeatsHold';
import { usePosReview } from './gql/queries/usePosReview';
import { PaymentOption } from './PaymentOption';
import { ReaderSelection } from './ReaderSelection';
import { TimerCountdown } from './TimerCountdown';

export const CartList = () => {
  const {
    tickets,
    total,
    setTotal,
    clearTicket,
    setDiscount,
    subtotal,
    setSubtotal,
    setAmount,
    setSeatsModalIsVisible,
    accountId,
    event,
    account: eventAccount,
    bestSeats,
    selected,
    seatId,
  } = useCart();

  const selectedSeatId = localStorage.getItem('selectedTicket');
  const selectedTicket = tickets[selected]?.tickets?.find(
    (ticket) => ticket.id === +selectedSeatId
  );

  const seat_ids = selectedTicket?.seat_ids ?? [seatId];

  const {
    state: { account },
  } = useContext(AppContext);

  const [isCheckoutVisible, setIsCheckoutVisible] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openReaderModal, setOpenReaderModal] = useState(false);

  const [fetchingTotal, setFetchingTotal] = useState(false);

  const releaseSeatsHold = useReleaseSeatsHold();
  const releaseBestAvailable = useReleaseBestAvailable();

  const OpenConfirmModal = () => {
    // Check if there is a saved reader in the cookie
    if (
      document.cookie
        .split(';')
        .some((item) => item.trim().startsWith(`${accountId}-reader`))
    ) {
      setIsCheckoutVisible(true);
    } else {
      // If there is no reader saved in the cookie, show the reader selection modal
      setOpenReaderModal(true);
    }
  };

  const OpenCashModal = () => {
    setOpenSuccessModal(true);
  };

  const history = useHistory();

  const pathname = useLocation().pathname;
  const userType =
    ['admin-employee', 'admin-track'].find((type) => pathname.includes(type)) ||
    'admin';

  const openSettings = () => {
    history.push(`/${userType}/events/posSettings/${event.id}`, {
      event: event,
    });
  };

  const params = useParams();
  const EventId = parseInt(params.id);

  const [allTickets, setAllTickets] = useState([]);

  const setTicketsDebounce = useDebounceCallback((newTickets) => {
    setAllTickets(newTickets);
  }, 1000);

  // Set allTickets to the tickets in the cart using debounce
  useEffect(() => {
    setFetchingTotal(true);

    const newTickets = Object.values(tickets).reduce((acc, value) => {
      const tickets = value.tickets?.map((ticket) => ({
        id: ticket.id,
        qty: ticket.count,
        ...(ticket.reserved_seat && ticket.count > 0
          ? {
              seats: ticket.seats?.map((seat) => ({
                seat_id: seat.id,
                ...(seat?.type ? { type: seat.type } : null),
              })),
            }
          : null),
      }));
      return [...acc, ...tickets];
    }, []);

    setTicketsDebounce(newTickets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets]);

  const { data, loading } = usePosReview(EventId, allTickets);

  useEffect(() => {
    if (!loading && data) {
      setTotal(data?.posReview?.subtotal);
      setSubtotal(data?.posReview?.subtotal);
      setDiscount(data?.posReview?.discount);
      setAmount(data?.posReview?.amount);

      setFetchingTotal(false);
    }
  }, [data, loading, setTotal, setDiscount, setSubtotal, setAmount]);

  const [timer, setTimer] = useState(600000);
  const seatsio = JSON.parse(sessionStorage.getItem('seatsio'));
  const holdToken = seatsio?.holdToken;

  const storedTimer = localStorage.getItem(`timer - ${holdToken}`);

  useEffect(() => {
    let interval;
    const timer = () => {
      const seatsio = JSON.parse(sessionStorage.getItem('seatsio'));
      const holdToken = seatsio?.holdToken;

      if (!holdToken) return;

      const storedTimer = localStorage.getItem(`timer - ${holdToken}`);

      let expiryTime;

      if (storedTimer) {
        expiryTime = parseInt(storedTimer, 10);

        interval = setInterval(() => {
          const timeLeft = expiryTime - new Date().getTime();
          if (timeLeft <= 0) {
            clearInterval(interval);
            clearTicket();
            setTimer(600000);

            // Remove any old timer keys
            Object.keys(localStorage).forEach((key) => {
              if (key.includes('timer')) localStorage.removeItem(key);
            });
          } else {
            setTimer(timeLeft);
          }
        }, 1000);
      }
    };

    timer();

    return () => clearInterval(interval);
  }, [setTimer, clearTicket]);

  const onClear = () => {
    onReleaseSeatsHold();
    if (bestSeats.size) onReleaseBestAvailable();

    clearTicket();
  };

  const onReleaseSeatsHold = async () => {
    const ticketsWithSeats = Object.values(tickets)
      .reduce((acc, value) => {
        const tickets = value.tickets?.map((ticket) => {
          const seats = ticket.seats
            ?.map((seat) => !bestSeats?.has(seat.id) && seat.id)
            .filter(Boolean);

          if (ticket.count > 0 && ticket.reserved_seat && seats?.length > 0)
            return {
              ticket_id: ticket.id,
              seat_ids: seats,
            };

          return null;
        });
        return [...acc, ...tickets];
      }, [])
      .filter(Boolean);

    if (!ticketsWithSeats.length) return;

    // Release the seat held based on the id
    const response = await releaseSeatsHold({
      event_id: event.id,
      tickets: ticketsWithSeats,
    });

    if (!response || response?.data?.releaseSeatsHold === null) {
      toast.error('Error releasing seats');
    }
  };

  const onReleaseBestAvailable = async () => {
    if (holdToken && bestSeats) {
      const response = await releaseBestAvailable({
        seats_event_id: seat_ids[0],
        selected_seats: Array.from(bestSeats),
      });

      if (!response || response?.data?.releaseBestAvailable === null) {
        toast.error('Error releasing seats');
      }
    }
  };

  const foundReservedSeat = Object.values(tickets).reduce((acc, value) => {
    const found = value.tickets?.some(
      (ticket) => ticket.reserved_seat && ticket.count > 0
    );

    return acc || found;
  }, false);

  const foundTickets = Object.values(tickets).reduce((acc, value) => {
    const found = value.tickets?.some(
      (ticket) => !ticket.reserved_seat && ticket.count > 0
    );

    return acc || found;
  }, false);

  const availableAccount = account || eventAccount;

  const posSettings =
    event && event?.pos_settings
      ? event?.pos_settings
      : availableAccount && availableAccount?.pos_settings
      ? availableAccount?.pos_settings
      : undefined;

  const cardOption =
    posSettings && posSettings?.payment
      ? posSettings?.payment === 'both' || posSettings?.payment === 'card'
      : true;

  const cashOption =
    posSettings && posSettings?.payment
      ? posSettings?.payment === 'both' || posSettings?.payment === 'cash'
      : true;

  // Count cart items
  const cartItemCount = Object.values(tickets).reduce((acc, value) => {
    return (
      acc +
      (value.tickets?.reduce((sum, ticket) => sum + (ticket.count || 0), 0) ||
        0)
    );
  }, 0);

  return (
    <MainContainer>
      {/* Cart Card */}
      <CartCard>
        <POSCardHeader>
          <CartTitle>
            Cart
            <CartBadge>{cartItemCount}</CartBadge>
          </CartTitle>
          <CartHeaderActions>
            {storedTimer && <TimerCountdown timer={timer} />}

            <ClearButton variant="danger" onClick={onClear}>
              <Icon icon="trash" size={14} color="currentColor" />
              Clear
            </ClearButton>

            <SettingsButton onClick={openSettings}>
              <Icon icon="settings-icon" size={20} color="currentColor" />
            </SettingsButton>
          </CartHeaderActions>
        </POSCardHeader>

        <CartBody scrollable>
          {/* Reserved Seats Section */}
          {foundReservedSeat && (
            <TicketSection>
              <SectionTitle>Reserved Seat</SectionTitle>
              {Object.values(tickets).map((value) =>
                value.tickets
                  ?.filter((ticket) => ticket.reserved_seat)
                  ?.map(
                    (ticket) =>
                      ticket.count > 0 &&
                      ticket.seats?.length > 0 &&
                      ticket.seats.map((seat) => (
                        <CartItem
                          ticket={ticket}
                          key={`${ticket.id}-${seat.id}`}
                          reserved={true}
                          seat={seat}
                          setIsVisible={setSeatsModalIsVisible}
                        />
                      ))
                  )
              )}
            </TicketSection>
          )}

          {/* Regular Tickets Section */}
          {foundTickets && (
            <TicketSection>
              <SectionTitle>Tickets</SectionTitle>
              {Object.values(tickets).map((value) =>
                value.tickets
                  ?.filter((ticket) => !ticket.reserved_seat)
                  ?.map(
                    (ticket) =>
                      ticket.count > 0 && (
                        <CartItem ticket={ticket} key={ticket.id} />
                      )
                  )
              )}
            </TicketSection>
          )}
        </CartBody>
      </CartCard>

      {/* Checkout Card */}
      <CheckoutCard>
        <CheckoutBody>
          <SummaryRow>
            <span>Subtotal</span>
            <span>{subtotal ?? '$0.00'}</span>
          </SummaryRow>
          <SummaryRow>
            <span>Fees</span>
            <span>$0.00</span>
          </SummaryRow>
          <SummaryRow isTotal>
            <span>Total</span>
            <span>{total ?? '$0.00'}</span>
          </SummaryRow>

          <PaymentGrid singleButton={!(cashOption && cardOption)}>
            {cardOption && (
              <PaymentButton
                variant="primary"
                onClick={OpenConfirmModal}
                disabled={fetchingTotal || !total}
              >
                <Icon icon="credit-card" size={18} color="currentColor" />
                Credit / Debit
              </PaymentButton>
            )}

            {cashOption && (
              <PaymentButton
                variant="outlined"
                onClick={OpenCashModal}
                disabled={fetchingTotal || !total}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                Cash
              </PaymentButton>
            )}
          </PaymentGrid>
        </CheckoutBody>
      </CheckoutCard>

      {/* Modals */}
      <PaymentOption
        isVisible={isCheckoutVisible}
        setIsVisible={setIsCheckoutVisible}
      />
      <CashPayment
        isVisible={openSuccessModal}
        setIsVisible={setOpenSuccessModal}
      />
      <ReaderSelection
        isVisible={openReaderModal}
        setIsVisible={setOpenReaderModal}
        setIsCheckoutVisible={setIsCheckoutVisible}
        asModal={true}
      />
    </MainContainer>
  );
};

// Cart Item Component
const CartItem = ({ ticket, reserved, seat, setIsVisible }) => {
  const { removeTicket, reservedSeatSection } = useCart();

  const onRemoveTicket = () => {
    ticket.reserved_seat ? setIsVisible(true) : removeTicket(ticket);
  };

  const type = ticket?.ticket_tiers?.find((tier) => tier.id === seat?.type);

  return (
    <CartItemContainer>
      <ItemQty>{reserved ? '1' : ticket?.count}</ItemQty>
      <ItemDetails>
        {reserved ? (
          reservedSeatSection ? (
            <>
              <ItemName>{`${type?.label ? `${type?.label} - ` : ''} ${
                ticket?.name
              }`}</ItemName>
              <ItemSubtext>{`${ticket?.reserved_seat_prefix} ${seat?.labels?.section}`}</ItemSubtext>
              <ItemSubtext>
                Row: <strong>{seat?.labels?.parent}</strong> - Seat:{' '}
                <strong>{seat?.labels?.own}</strong>
              </ItemSubtext>
            </>
          ) : (
            <>
              <ItemName>{ticket?.name}</ItemName>
              <ItemSubtext>
                {ticket?.reserved_seat_prefix} {seat?.labels?.own}
              </ItemSubtext>
            </>
          )
        ) : (
          <ItemName>{ticket?.name}</ItemName>
        )}
      </ItemDetails>
      <ItemPrice>
        {reserved ? seat?.price : `$${ticket?.price?.toFixed(2)}`}
      </ItemPrice>
      <RemoveButton onClick={onRemoveTicket}>
        <Icon icon="trash" size={16} color="currentColor" />
      </RemoveButton>
    </CartItemContainer>
  );
};

// Styled Components
const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 1024px) {
    height: auto;
    min-height: 400px;
    overflow: visible;
  }
`;

const CartCard = styled(POSCard)`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const CartBody = styled(POSCardBody)`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  min-height: 0;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)'};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.2)'
        : 'rgba(0, 0, 0, 0.2)'};
    border-radius: 3px;
  }
`;

const CartTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#000033'};
`;

const CartBadge = styled.span`
  background: ${(props) => props.theme.colors?.primary || '#FA4616'};
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 20px;
`;

const CartHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ClearButton = styled(POSButton)`
  padding: 8px 12px;
  font-size: 12px;
`;

const SettingsButton = styled.button`
  background: transparent;
  border: none;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.secondary || '#94A3B8'
      : '#64748B'};
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.05)'};
    color: ${(props) => props.theme.colors?.primary || '#FA4616'};
  }
`;

const TicketSection = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc'};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#000033'};
`;

const CartItemContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border-radius: 8px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ItemQty = styled.span`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.1)'};
  color: ${(props) => props.theme.colors?.primary || '#FA4616'};
  padding: 6px 10px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 13px;
  min-width: 32px;
  text-align: center;
`;

const ItemDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ItemName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#000033'};
`;

const ItemSubtext = styled.span`
  font-size: 12px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.secondary || '#94A3B8'
      : '#64748B'};

  strong {
    font-weight: 600;
  }
`;

const ItemPrice = styled.span`
  font-weight: 700;
  font-size: 14px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#000033'};
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.muted || '#64748B'
      : '#94A3B8'};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: ${(props) => props.theme.colors?.error || '#EF4444'};
  }
`;

// Checkout Card Styles
const CheckoutCard = styled(POSCard)`
  flex-shrink: 0;
`;

const CheckoutBody = styled.div`
  padding: 20px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 20px;
  font-weight: 500;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#000033'};

  ${(props) =>
    props.isTotal &&
    `
    font-size: 28px;
    font-weight: 700;
    margin-top: 20px;
    margin-bottom: 0;
    padding-top: 20px;
    border-top: 1px solid ${
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'
    };
  `}
`;

const PaymentGrid = styled.div`
  display: grid;
  grid-template-columns: ${(props) => (props.singleButton ? '1fr' : '1fr 1fr')};
  gap: 12px;
  margin-top: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PaymentButton = styled(POSButton)`
  padding: 16px;
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;
